import React from 'react';
import {
  EmptyState,
} from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Button,
  Box,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';

/* ─── Types ─── */

interface LokiStream {
  stream: Record<string, string>;
  values: [string, string][]; // [nanosecond_timestamp, log_line]
}

interface LogEntry {
  timestamp: string;
  timestampNano: string;
  line: string;
  labels: Record<string, string>;
  level: string;
  pod: string;
  container: string;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1),
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, monospace',
    fontSize: '12px',
    lineHeight: '1.5',
    padding: theme.spacing(1),
    borderRadius: 4,
    maxHeight: 'calc(100vh - 350px)',
    minHeight: 400,
    overflowY: 'auto',
    overflowX: 'auto',
    position: 'relative' as const,
  },
  logLine: {
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
    padding: '1px 4px',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.05)',
    },
  },
  timestamp: {
    color: '#858585',
    marginRight: 8,
    userSelect: 'none' as const,
  },
  pod: {
    color: '#569cd6',
    marginRight: 8,
    userSelect: 'none' as const,
  },
  levelError: { color: '#f44747', fontWeight: 700 },
  levelWarn: { color: '#cca700' },
  levelInfo: { color: '#6a9955' },
  levelDebug: { color: '#858585' },
  levelDefault: { color: '#d4d4d4' },
  searchHighlight: {
    backgroundColor: 'rgba(255, 213, 0, 0.3)',
  },
  statusBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.type === 'dark' ? '#252526' : '#f3f3f3',
    borderRadius: '0 0 4px 4px',
    fontSize: 12,
  },
}));

/* ─── Helpers ─── */

function parseLevel(line: string, labels: Record<string, string>): string {
  if (labels.level) return labels.level.toUpperCase();
  if (labels.detected_level) return labels.detected_level.toUpperCase();
  const upper = line.substring(0, 200).toUpperCase();
  if (upper.includes('ERROR') || upper.includes('SEVERE') || upper.includes('FATAL')) return 'ERROR';
  if (upper.includes('WARN')) return 'WARN';
  if (upper.includes('DEBUG') || upper.includes('TRACE')) return 'DEBUG';
  if (upper.includes('INFO')) return 'INFO';
  return '';
}

function formatTimestamp(nanoStr: string): string {
  const ms = parseInt(nanoStr, 10) / 1e6;
  const d = new Date(ms);
  return `${d.toLocaleTimeString('ko-KR', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
     }.${  String(d.getMilliseconds()).padStart(3, '0')}`;
}

function parseLookback(val: string): number {
  const m = val.match(/^(\d+)(m|h|d)$/);
  if (!m) return 3600;
  const n = parseInt(m[1], 10);
  switch (m[2]) {
    case 'm': return n * 60;
    case 'h': return n * 3600;
    case 'd': return n * 86400;
    default: return 3600;
  }
}

/* ─── Component ─── */

export const LogsContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);

  const ns =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ?? '';
  const entityName = entity?.metadata?.name ?? '';
  // Pod name prefix override — entity name ≠ Pod prefix 케이스 (예: arcus-dev entity ↔ arcus-portal-* Pod)
  // 없으면 entity name 그대로 사용
  const podPrefix =
    entity?.metadata?.annotations?.['arcus.dev/pod-prefix'] ?? entityName;

  /* ─── State ─── */
  const [logs, setLogs] = React.useState<LogEntry[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [lookback, setLookback] = React.useState('15m');
  const [limit, setLimit] = React.useState(500);
  const [searchText, setSearchText] = React.useState('');
  const [levelFilter, setLevelFilter] = React.useState('ALL');
  const [autoRefresh, setAutoRefresh] = React.useState(false);
  const [newestFirst, setNewestFirst] = React.useState(true);
  const [podFilter, setPodFilter] = React.useState('ALL');
  const [containerFilter, setContainerFilter] = React.useState('ALL');
  const [knownPods, setKnownPods] = React.useState<string[]>([]);
  // Pod 범위: active = 활성(Running)+직전(최근 10분 내 로그를 남기고 종료된 Pod)만 / all = 기간 내 전체
  // workday 피드백 #1 — 구 CrashLoop/재시작 Pod 노출 과다 해소 (기본 active)
  const [podScope, setPodScope] = React.useState<'active' | 'all'>('active');
  const [runningPods, setRunningPods] = React.useState<string[]>([]);
  const [recentLokiPods, setRecentLokiPods] = React.useState<string[]>([]);

  const logContainerRef = React.useRef<HTMLDivElement>(null);

  /* ─── Authenticated fetch ─── */
  const authFetch = React.useCallback(async (url: string) => {
    const { token } = await identityApi.getCredentials();
    if (!token) {
      setSessionExpired(true);
      return undefined;
    }
    setSessionExpired(false);
    const resp = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.status === 401) {
      setSessionExpired(true);
      return undefined;
    }
    return resp;
  }, [identityApi]);

  /* ─── Fetch Pod list from Loki labels API ─── */
  const fetchPodList = React.useCallback(async () => {
    if (!ns) return;
    try {
      const now = Date.now() * 1_000_000;
      const start = now - parseLookback(lookback) * 1_000_000_000;
      const query = podPrefix
        ? `{namespace="${ns}", pod=~"${podPrefix}.*"}`
        : `{namespace="${ns}"}`;
      const params = new URLSearchParams({
        query,
        start: String(start),
        end: String(now),
      });
      const url = `/api/proxy/loki/loki/api/v1/label/pod/values?${params.toString()}`;
      const resp = await authFetch(url);
      if (!resp || !resp.ok) return;
      const data = await resp.json();
      const pods: string[] = (data?.data || []).sort();
      setKnownPods(pods);

      // 최근 10분 창 — "직전 Pod"(방금 종료됐지만 최근까지 로그를 남긴 Pod) 판별용
      const recentStart = now - 10 * 60 * 1_000_000_000;
      const recentParams = new URLSearchParams({
        query,
        start: String(recentStart),
        end: String(now),
      });
      const recentResp = await authFetch(
        `/api/proxy/loki/loki/api/v1/label/pod/values?${recentParams.toString()}`,
      );
      if (recentResp && recentResp.ok) {
        const recentData = await recentResp.json();
        setRecentLokiPods((recentData?.data || []) as string[]);
      }
    } catch {
      // Pod list fetch failure is non-critical; fall back to log-derived pods
    }
  }, [ns, podPrefix, lookback, authFetch]);

  /* ─── Fetch live(Running) Pod list from K8s (kubernetes-infra) ─── */
  const fetchRunningPods = React.useCallback(async () => {
    if (!ns) return;
    try {
      const params = new URLSearchParams({ namespace: ns });
      if (podPrefix) params.set('prefix', podPrefix);
      const resp = await authFetch(
        `/api/kubernetes-infra/pods?${params.toString()}`,
      );
      if (!resp || !resp.ok) return;
      const data = await resp.json();
      const running = ((data?.pods || []) as Array<{ name: string; phase: string }>)
        .filter(p => p.phase === 'Running')
        .map(p => p.name);
      setRunningPods(running);
    } catch {
      // 실패 시 scope 필터는 Loki 목록으로 자연 강등 (non-critical)
    }
  }, [ns, podPrefix, authFetch]);

  /* ─── Pod scope 파생: 활성(Running) + 직전(최근 10분 로그 & 비Running) ─── */
  const prevPods = React.useMemo(() => {
    const running = new Set(runningPods);
    return recentLokiPods.filter(p => !running.has(p));
  }, [runningPods, recentLokiPods]);

  const scopedPods = React.useMemo(() => {
    if (podScope === 'all') return null; // null = 제한 없음
    const set = new Set<string>([...runningPods, ...prevPods]);
    // K8s/Loki 조회 둘 다 빈 경우(실패/초기) = 제한 해제로 강등 (로그 공백 방지)
    if (set.size === 0) return null;
    return Array.from(set);
  }, [podScope, runningPods, prevPods]);

  /* ─── Fetch Logs ─── */
  const fetchLogs = React.useCallback(async () => {
    if (!ns) return;
    setLoading(true);
    setError(null);
    try {
      // Loki API: 나노초 타임스탬프 (Date.now()는 밀리초 → ×1e6)
      const now = Date.now() * 1_000_000;
      const start = now - parseLookback(lookback) * 1_000_000_000;

      // LogQL: namespace + 서비스명 prefix 필터, Pod 선택 시 정확 매칭
      let query: string;
      if (podFilter !== 'ALL') {
        query = `{namespace="${ns}", pod="${podFilter}"}`;
      } else if (scopedPods && scopedPods.length > 0) {
        // 활성+직전 범위 — pod 이름 정확 union (이름은 [a-z0-9-] 라 regex 이스케이프 불요)
        query = `{namespace="${ns}", pod=~"${scopedPods.join('|')}"}`;
      } else if (podPrefix) {
        query = `{namespace="${ns}", pod=~"${podPrefix}.*"}`;
      } else {
        query = `{namespace="${ns}"}`;
      }

      const params = new URLSearchParams({
        query,
        start: String(start),
        end: String(now),
        limit: String(limit),
        direction: 'backward', // newest first from API
      });

      const url = `/api/proxy/loki/loki/api/v1/query_range?${params.toString()}`;
      const resp = await authFetch(url);
      if (!resp) return;
      if (!resp.ok) {
        throw new Error(`Loki API error: ${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();

      const entries: LogEntry[] = [];
      const streams: LokiStream[] = data?.data?.result || [];

      for (const stream of streams) {
        const labels = stream.stream || {};
        const podName = labels.pod || labels.instance || '';
        const container = labels.container || '';

        for (const [ts, line] of stream.values) {
          entries.push({
            timestamp: formatTimestamp(ts),
            timestampNano: ts,
            line,
            labels,
            level: parseLevel(line, labels),
            pod: podName,
            container,
          });
        }
      }

      // Sort by timestamp (API returns backward, re-sort for consistency)
      entries.sort((a, b) => {
        const cmp = BigInt(b.timestampNano) - BigInt(a.timestampNano);
        return cmp > 0n ? 1 : cmp < 0n ? -1 : 0;
      });

      setLogs(entries);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ns, podPrefix, lookback, limit, podFilter, scopedPods, authFetch]);

  React.useEffect(() => {
    fetchPodList();
  }, [fetchPodList]);

  React.useEffect(() => {
    fetchRunningPods();
  }, [fetchRunningPods]);

  React.useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh
  React.useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(fetchLogs, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // Scroll to top when newest first and new data
  React.useEffect(() => {
    if (newestFirst && logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  }, [logs, newestFirst]);

  /* ─── Available pods & containers ─── */
  const availablePods = React.useMemo(() => {
    const set = new Set<string>(knownPods);
    for (const l of logs) {
      if (l.pod) set.add(l.pod);
    }
    // 활성+직전 모드 = 범위 밖(과거) Pod 은 드롭다운에서 제외
    if (scopedPods) {
      const scoped = new Set(scopedPods);
      for (const p of Array.from(set)) {
        if (!scoped.has(p)) set.delete(p);
      }
      for (const p of scopedPods) set.add(p);
    }
    const pods = Array.from(set);

    // 각 Pod의 최신 로그 타임스탬프를 수집하여 활성도 판단
    const lastSeen = new Map<string, string>();
    for (const l of logs) {
      if (l.pod && (!lastSeen.has(l.pod) || l.timestampNano > lastSeen.get(l.pod)!)) {
        lastSeen.set(l.pod, l.timestampNano);
      }
    }

    // 최신 로그가 있는 Pod를 먼저, 그 안에서 타임스탬프 내림차순
    return pods.sort((a, b) => {
      const tsA = lastSeen.get(a) ?? '0';
      const tsB = lastSeen.get(b) ?? '0';
      return tsA > tsB ? -1 : tsA < tsB ? 1 : a.localeCompare(b);
    });
  }, [logs, knownPods, scopedPods]);

  const availableContainers = React.useMemo(() => {
    const set = new Set<string>();
    for (const l of logs) {
      if (l.container) set.add(l.container);
    }
    return Array.from(set).sort();
  }, [logs]);

  // Reset pod/container filter when options change
  React.useEffect(() => {
    if (podFilter !== 'ALL' && !availablePods.includes(podFilter)) {
      setPodFilter('ALL');
    }
  }, [availablePods, podFilter]);

  React.useEffect(() => {
    if (containerFilter !== 'ALL' && !availableContainers.includes(containerFilter)) {
      setContainerFilter('ALL');
    }
  }, [availableContainers, containerFilter]);

  /* ─── Filtered & sorted logs ─── */
  const filteredLogs = React.useMemo(() => {
    let result = logs;

    if (podFilter !== 'ALL') {
      result = result.filter(l => l.pod === podFilter);
    }

    if (containerFilter !== 'ALL') {
      result = result.filter(l => l.container === containerFilter);
    }

    if (levelFilter !== 'ALL') {
      result = result.filter(l => l.level === levelFilter);
    }

    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter(l => l.line.toLowerCase().includes(lower));
    }

    if (!newestFirst) {
      result = [...result].reverse();
    }

    return result;
  }, [logs, podFilter, containerFilter, levelFilter, searchText, newestFirst]);

  /* ─── Level counts ─── */
  const levelCounts = React.useMemo(() => {
    const counts: Record<string, number> = { ERROR: 0, WARN: 0, INFO: 0, DEBUG: 0 };
    for (const l of logs) {
      if (counts[l.level] !== undefined) counts[l.level]++;
    }
    return counts;
  }, [logs]);

  /* ─── Level style ─── */
  const getLevelClass = (level: string) => {
    switch (level) {
      case 'ERROR': case 'SEVERE': case 'FATAL': return classes.levelError;
      case 'WARN': case 'WARNING': return classes.levelWarn;
      case 'INFO': return classes.levelInfo;
      case 'DEBUG': case 'TRACE': return classes.levelDebug;
      default: return classes.levelDefault;
    }
  };

  /* ─── Early returns ─── */
  if (!ns) {
    return (
      <EmptyState
        title="No namespace configured"
        missing="info"
        description="backstage.io/kubernetes-namespace annotation is required to display logs."
      />
    );
  }

  if (sessionExpired) {
    return (
      <EmptyState
        title="세션이 만료되었습니다"
        missing="info"
        description="페이지를 새로고침하면 정상 동작합니다."
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            새로고침
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      {/* ─── Toolbar ─── */}
      <Box className={classes.toolbar}>
        <TextField
          select
          label="Period"
          value={lookback}
          onChange={e => setLookback(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 100 }}
        >
          {['5m', '15m', '30m', '1h', '3h', '6h', '12h', '1d'].map(v => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Limit"
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
          variant="outlined"
          size="small"
          style={{ minWidth: 90 }}
        >
          {[100, 200, 500, 1000, 2000].map(v => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Pod 범위"
          value={podScope}
          onChange={e => setPodScope(e.target.value as 'active' | 'all')}
          variant="outlined"
          size="small"
          style={{ minWidth: 130 }}
        >
          <MenuItem value="active">활성+직전</MenuItem>
          <MenuItem value="all">전체 (기간 내)</MenuItem>
        </TextField>

        {availablePods.length > 0 && (
          <TextField
            select
            label="Pod"
            value={podFilter}
            onChange={e => setPodFilter(e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 180 }}
          >
            <MenuItem value="ALL">All Pods ({availablePods.length})</MenuItem>
            {availablePods.map(p => {
              const mark = runningPods.includes(p)
                ? '🟢 '
                : prevPods.includes(p)
                  ? '⏪ '
                  : '';
              return (
                <MenuItem key={p} value={p}>
                  {mark}
                  {p.length > 45 ? `...${p.slice(-42)}` : p}
                </MenuItem>
              );
            })}
          </TextField>
        )}

        {availableContainers.length > 1 && (
          <TextField
            select
            label="Container"
            value={containerFilter}
            onChange={e => setContainerFilter(e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 130 }}
          >
            <MenuItem value="ALL">All ({availableContainers.length})</MenuItem>
            {availableContainers.map(c => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          label="Level"
          value={levelFilter}
          onChange={e => setLevelFilter(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 100 }}
        >
          <MenuItem value="ALL">ALL</MenuItem>
          <MenuItem value="ERROR">ERROR ({levelCounts.ERROR})</MenuItem>
          <MenuItem value="WARN">WARN ({levelCounts.WARN})</MenuItem>
          <MenuItem value="INFO">INFO ({levelCounts.INFO})</MenuItem>
          <MenuItem value="DEBUG">DEBUG ({levelCounts.DEBUG})</MenuItem>
        </TextField>

        <TextField
          label="Search"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          variant="outlined"
          size="small"
          style={{ minWidth: 200 }}
          placeholder="Filter logs..."
        />

        <Tooltip title={newestFirst ? 'Newest first' : 'Oldest first'}>
          <IconButton size="small" onClick={() => setNewestFirst(!newestFirst)}>
            {newestFirst ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>

        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={<Typography variant="caption">Auto (10s)</Typography>}
        />

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchLogs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ─── Error ─── */}
      {error && (
        <Paper style={{ padding: 8, marginBottom: 8, backgroundColor: '#ffebee' }}>
          <Typography color="error" variant="body2">{error}</Typography>
        </Paper>
      )}

      {/* ─── Log Viewer ─── */}
      <div ref={logContainerRef} className={classes.logContainer}>
        {loading && logs.length === 0 && (
          <Typography style={{ color: '#858585', padding: 16 }}>Loading logs...</Typography>
        )}
        {!loading && filteredLogs.length === 0 && (
          <Typography style={{ color: '#858585', padding: 16 }}>
            No logs found for namespace <strong>{ns}</strong> in the last {lookback}.
          </Typography>
        )}
        {filteredLogs.map((entry, i) => (
          <div key={`${entry.timestampNano}-${i}`} className={classes.logLine}>
            <span className={classes.timestamp}>{entry.timestamp}</span>
            {entry.pod && (
              <span className={classes.pod}>
                {entry.pod.length > 30 ? `...${entry.pod.slice(-27)}` : entry.pod}
              </span>
            )}
            {entry.level && (
              <span className={getLevelClass(entry.level)} style={{ marginRight: 8, minWidth: 40, display: 'inline-block' }}>
                {entry.level.padEnd(5)}
              </span>
            )}
            <span className={classes.levelDefault}>{entry.line}</span>
          </div>
        ))}
      </div>

      {/* ─── Status Bar ─── */}
      <Box className={classes.statusBar}>
        <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Typography variant="caption">
            {filteredLogs.length} / {logs.length} lines
          </Typography>
          {levelCounts.ERROR > 0 && (
            <Chip size="small" label={`${levelCounts.ERROR} errors`} style={{ backgroundColor: '#f44747', color: '#fff', height: 20 }} />
          )}
          {levelCounts.WARN > 0 && (
            <Chip size="small" label={`${levelCounts.WARN} warns`} style={{ backgroundColor: '#cca700', color: '#000', height: 20 }} />
          )}
        </Box>
        <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {autoRefresh && (
            <Chip
              size="small"
              icon={<PlayArrowIcon style={{ fontSize: 14 }} />}
              label="Live"
              color="primary"
              style={{ height: 20 }}
            />
          )}
          <Typography variant="caption" style={{ color: '#858585' }}>
            {newestFirst ? 'Newest first' : 'Oldest first'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
